import React from 'react';
import PropTypes from 'prop-types';
import { LoadingSpinner } from 'components/common';
import { FiGlobe, FiBriefcase, FiMap, FiMapPin } from 'react-icons/fi';
import ConversionFunnelChart from 'components/app/crm/dashboard/sales/ConversionFunnelChart';
import QuotationStagesChart from 'components/app/crm/dashboard/sales/QuotationStagesChart';
import QuotationTrendChart from 'components/app/crm/dashboard/quotations/QuotationTrendChart';
import QuotationBreakdownChart from 'components/app/crm/dashboard/quotations/QuotationBreakdownChart';
import OverviewDetailGrid from '../OverviewDetailGrid';

// Quotation tab — full quotation analytics: funnel + stages, monthly trend,
// region / sales-unit / country / province breakdowns, then the detail grid.
// Funnel drills via the shared detail drawer (funnel_stage is honoured by the
// Overview detail endpoint); dimension breakdowns are display-only.
const QuotationTab = ({ data, loading, baseParams, onDrill, groupBy, onGroupByChange }) => {
  if (loading && !data) return <div className="tw-text-center tw-py-12"><LoadingSpinner /></div>;

  return (
    <>
      <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3">
        <div className="tw-col-span-12 xl:tw-col-span-7">
          <ConversionFunnelChart
            data={data?.funnel}
            loading={loading}
            subtitle="Quotation → Negotiation → Won"
            onDrillDown={(title, extra) => onDrill({ section: 'quotation', title, extraParams: extra })}
          />
        </div>
        <div className="tw-col-span-12 xl:tw-col-span-5">
          <QuotationStagesChart data={data?.stages} loading={loading} />
        </div>
      </div>

      <div className="tw-mb-3">
        <QuotationTrendChart
          title="Quotation Trend"
          data={data?.trend}
          loading={loading}
          groupBy={groupBy}
          onGroupByChange={onGroupByChange}
        />
      </div>

      <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3">
        <div className="tw-col-span-12 lg:tw-col-span-6">
          <QuotationBreakdownChart title="By Sales Region" Icon={FiGlobe} iconColor="tw-text-emerald-500"
            rowLabel="Region" labelKey="region" drillKey="region" drillTitlePrefix="Region"
            data={data?.region} loading={loading} fullHeight />
        </div>
        <div className="tw-col-span-12 lg:tw-col-span-6">
          <QuotationBreakdownChart title="By Sales Unit" Icon={FiBriefcase} iconColor="tw-text-blue-500"
            rowLabel="Sales Unit" labelKey="unit_name" subLabelKey="region" drillKey="unit_name" drillTitlePrefix="Sales Unit"
            data={data?.sales_unit} loading={loading} fullHeight />
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-3">
        <div className="tw-col-span-12 lg:tw-col-span-6">
          <QuotationBreakdownChart title="By Country" Icon={FiMap} iconColor="tw-text-cyan-500"
            rowLabel="Country" labelKey="country" drillKey="country" drillTitlePrefix="Country"
            data={data?.country} loading={loading} fullHeight />
        </div>
        <div className="tw-col-span-12 lg:tw-col-span-6">
          <QuotationBreakdownChart title="By Province" Icon={FiMapPin} iconColor="tw-text-rose-500"
            rowLabel="Province" labelKey="province" subLabelKey="country" drillKey="province" drillTitlePrefix="Province"
            data={data?.province} loading={loading} fullHeight />
        </div>
      </div>

      <OverviewDetailGrid section="quotation" title="Quotation Records" baseParams={baseParams} />
    </>
  );
};

QuotationTab.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  baseParams: PropTypes.object.isRequired,
  onDrill: PropTypes.func.isRequired,
  groupBy: PropTypes.string,
  onGroupByChange: PropTypes.func
};
QuotationTab.defaultProps = { data: null, loading: false, groupBy: 'month', onGroupByChange: () => {} };

export default QuotationTab;
